import { defineStore } from 'pinia'
import { getConfigCat } from '~/utils/config'
import { useLocaleStore } from '~/stores/locale'
import { RegionAndCountryFields } from '~/types/locale'
import { usePersistentStore } from '~/stores/persistent'
import type { AppRuntimeConfig } from '~/utils/runtimeConfig'

export const featureKeys = {
    paypalEnabled: {
      key: 'paypal',
      region: true
    },
    disableCreditEnabled: {
      key: 'disableCredit',
      region: true
    },
    // Opt-out kill switch, absent by default. Deliberately NOT `recaptchaEnabled`:
    // an opt-IN flag made "not configured" read the same as "deliberately off", which
    // let the frontend stop minting while the backend still enforced. AB#63069.
    recaptchaDisabled: {
      key: 'recaptchaDisabled'
    },
    bancontactEnabled: {
      key: 'bancontact'
    },
    idealEnabled: {
      key: 'ideal'
    },
    paylaterEnabled: {
      key: 'paylater'
    },
    paypalCreditCardEnabled: {
      key: 'paypalCreditCard',
      region: true
    },
    recurlyPaypalSubscriptionsEnabled: {
      key: 'recurlyPaypalSubscriptions',
      region: true
    },
    autocompleteEnabled: {
      key: 'autocomplete',
      region: true
    },
    addressValidationEnabled: {
      key: 'address-validate',
      region: true
    },
    cancelSubscriptionEnabled: {
      key: 'cancelSubscription'
    },
    newsletterSignupEnabled: {
      // Token name matches nuxt2 exactly: the property is `...Enabled`, the ConfigCat /
      // ENABLED_FEATURES token is not (nuxt2 PR 21008: "Enable with newsletterSignup").
      key: 'newsletterSignup'
    },
    walletManagementRestricted: {
      key: 'walletManagementRestricted'
    },
    contentSearch: {
      key: 'contentSearch'
    }
  }

export const useFeaturesStore = defineStore('features', () => {
  const enabledFeaturesLoaded = ref<Set<string>>()

  const localeStore = useLocaleStore()
  const persistentStore = usePersistentStore()
  const boot = async ($config: AppRuntimeConfig) => {
    const loadedFeatures = await (await getConfigCat($config)).getValueAsync('enabledFeatures', '')

    enabledFeaturesLoaded.value = new Set([
      ...`${($config.public.ENABLED_FEATURES || '')},${loadedFeatures}`.split(',').filter(i => i)
    ])
  }

  const enabledFeatures = computed(() => {
    const enabledFeature = persistentStore.getPersistentValue('enabledFeature')
    const persistentFeatures = Array.isArray(enabledFeature)
      ? enabledFeature
      : enabledFeature
        ? [enabledFeature]
        : []
    return new Set([...(enabledFeaturesLoaded.value || []), ...persistentFeatures])
  })
  const countryCode = computed(() => localeStore.country().countryCode.toLowerCase())
  const localeEnabledFeatures = computed(() => {
    const features = [...((localeStore.region())[RegionAndCountryFields.enabledFeatures] || []), ...((localeStore.country())[RegionAndCountryFields.enabledFeatures] || [])]
    return [...new Set(features)]
  })

  const { user, isDemo } = useSAuthState()

  // true for non demo roles
  const paypalEnabled = computed(() => !!enabledFeatures.value?.has(`${featureKeys.paypalEnabled.key}-${countryCode.value}`) || localeEnabledFeatures.value.includes(featureKeys.paypalEnabled.key))
  const disableCreditEnabled = computed(() => !!enabledFeatures.value?.has(`${featureKeys.disableCreditEnabled.key}-${countryCode.value}`))
  const bancontactEnabled = computed(() => (!!enabledFeatures.value?.has(featureKeys.bancontactEnabled.key) || localeEnabledFeatures.value.includes(featureKeys.bancontactEnabled.key)) && localeStore.countryIsBe)
  const idealEnabled = computed(() => (!!enabledFeatures.value?.has(featureKeys.idealEnabled.key) || localeEnabledFeatures.value.includes(featureKeys.idealEnabled.key)) && localeStore.countryIsNl)
  const paylaterEnabled = computed(() =>
    (!!enabledFeatures.value?.has(featureKeys.paylaterEnabled.key) || localeEnabledFeatures.value.includes(featureKeys.paylaterEnabled.key)) &&
    (localeStore.countryIsAu || localeStore.countryIsDe || localeStore.countryIsFr || localeStore.countryIsUk)
  )
  const payIn4MessagingEnabled = computed(() => paypalEnabled.value && paylaterEnabled.value)
  const paypalCreditCardEnabled = computed(() => (!!enabledFeatures.value?.has(featureKeys.paypalCreditCardEnabled.key) || localeEnabledFeatures.value.includes(featureKeys.paypalCreditCardEnabled.key)))
  const recurlyPaypalSubscriptionsEnabled = computed(() => !!enabledFeatures.value?.has(`${featureKeys.recurlyPaypalSubscriptionsEnabled.key}-${countryCode.value}`) || localeEnabledFeatures.value.includes(featureKeys.recurlyPaypalSubscriptionsEnabled.key))
  const autocompleteEnabled = computed(() => !!enabledFeatures.value?.has(`${featureKeys.autocompleteEnabled.key}-${countryCode.value}`) || localeEnabledFeatures.value.includes(featureKeys.autocompleteEnabled.key))
  const addressValidationEnabled = computed(() => !!enabledFeatures.value?.has(`${featureKeys.addressValidationEnabled.key}-${countryCode.value}`) || localeEnabledFeatures.value.includes(featureKeys.addressValidationEnabled.key))
  const isBetaUser = computed(() => !!user.value.isBetaUser)
  const recaptchaDisabled = computed(() => !!enabledFeatures.value?.has(featureKeys.recaptchaDisabled.key))
  const cancelSubscriptionEnabled = computed(() => !!enabledFeatures.value?.has(featureKeys.cancelSubscriptionEnabled.key))
  const newsletterSignupEnabled = computed(() => !!enabledFeatures.value?.has(featureKeys.newsletterSignupEnabled.key))
  const contentSearchEnabled = computed(() => !!enabledFeatures.value?.has(featureKeys.contentSearch.key))
  const walletManagementRestricted = computed(() => !!enabledFeatures.value?.has(featureKeys.walletManagementRestricted.key))
  // Gates the EU click-to-cancel entry point in the footer (CancelOrderBlock). Mirrors
  // Nuxt2: an EU market and a non-demonstrator session are both required.
  // isDemo ≡ Nuxt2 rootStore.isDemo (Demonstrator, or Agent with persistent isDemo).
  const cancelOrderRequestEnabled = computed(() =>
    localeStore.isEuCountry &&
    !isDemo.value
  )

  return {
    enabledFeatures,
    localeEnabledFeatures,
    isBetaUser,
    // need to return so its state shared between ssr and client since we only boot on ssr from configcat
    enabledFeaturesLoaded,
    recaptchaDisabled,
    addressValidationEnabled,
    autocompleteEnabled,
    paypalEnabled,
    disableCreditEnabled,
    bancontactEnabled,
    idealEnabled,
    paylaterEnabled,
    payIn4MessagingEnabled,
    paypalCreditCardEnabled,
    recurlyPaypalSubscriptionsEnabled,
    cancelSubscriptionEnabled,
    newsletterSignupEnabled,
    contentSearchEnabled,
    walletManagementRestricted,
    cancelOrderRequestEnabled,
    boot
  }
})
